import { useEffect, useRef, useState } from 'react'
import { RotateCcw, Save, FileText } from 'lucide-react'
import { DEFAULT_PDF_LAYOUT, PDF_DRAGGABLE_ELEMENTS, PDF_ELEMENT_LABELS, type PdfLayout } from '@mavatech/shared'
import { Button, Segmented } from '@/components/ui'
import { trpc } from '@/lib/trpc'
import { useAppStore } from '@/stores/app.store'

/**
 * Visueller PDF-Template-Editor: links eine Live-Vorschau (iframe, dieselbe HTML/CSS-Vorlage wie
 * das echte PDF — siehe apps/api/src/pdf/html-template.ts), rechts ein Eigenschaften-Panel für das
 * gerade ausgewählte Element. Kopfbereich-Elemente (Logo/Absenderzeile/Empfänger/Info-Kasten/
 * Titelblock) lassen sich per Drag&Drop verschieben; Positionstabelle/Summenblock/Fußzeile bleiben
 * Fließtext (Seitenumbruch), sind aber in Schriftgröße/Sichtbarkeit einstellbar. Elementnamen sind
 * bewusst identisch zu PDF_ELEMENT_LABELS im Code — wer "im Summenblock" sagt, meint überall
 * dasselbe Element.
 */

type ElKey = keyof typeof PDF_ELEMENT_LABELS
const DRAGGABLE = new Set<string>(PDF_DRAGGABLE_ELEMENTS as readonly string[])
/** Etwas dunklerer Farbton — für die Fußzeile, muss zum `darken()`-Helfer in html-template.ts
 * passen, damit die Live-Vorschau beim Ändern der Akzentfarbe konsistent bleibt. */
function darkenHex(hex: string, amount = 0.16): string {
  const h = hex.replace('#', '')
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return hex
  const c = (i: number) =>
    Math.max(0, Math.round(parseInt(h.slice(i, i + 2), 16) * (1 - amount)))
      .toString(16)
      .padStart(2, '0')
  return `#${c(0)}${c(2)}${c(4)}`
}

/** A4-Breite in CSS-Pixeln (595.28pt × 96/72) — das Dokument selbst ist immer genau so breit
 * (siehe body-Regel in html-template.ts); der umgebende Kasten hat aber eine ganz andere Form
 * (breiter als hoch), deshalb wird hier per `zoom` passend herunter-/hochskaliert statt den Inhalt
 * einfach zu strecken (das führte zu einer verzerrten Querformat-Ansicht). */
const A4_WIDTH_PX = 793.71

function NumField({
  label,
  value,
  onChange,
  step = 1,
}: {
  label: string
  value: number
  onChange: (n: number) => void
  step?: number
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-text-secondary">
      {label}
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="bg-bg-input border border-border rounded px-2 py-1.5 text-sm text-text-primary"
      />
    </label>
  )
}

function CheckField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (b: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-text-secondary">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  )
}

export function PdfLayoutTab() {
  const addToast = useAppStore((s) => s.addToast)
  const utils = trpc.useUtils()
  const stored = trpc.pdfLayout.get.useQuery()

  const [L, setL] = useState<PdfLayout>(DEFAULT_PDF_LAYOUT)
  const [loaded, setLoaded] = useState(false)
  const [which, setWhich] = useState<'invoice' | 'quote'>('invoice')
  const [selected, setSelected] = useState<ElKey | null>(null)
  const [snapshot, setSnapshot] = useState<{ variant: 'invoice' | 'quote'; layout: PdfLayout } | null>(null)
  // Erzwingt einen kompletten iframe-Neuaufbau bei jedem neuen Snapshot (als React-`key`) — verlässt
  // sich NICHT darauf, dass sich der HTML-String ändert: fällt ein „Zurücksetzen" zufällig mit einem
  // bereits zuvor geladenen HTML zusammen (z. B. Rücksprung auf die Werksmaße), bliebe sonst jede
  // per postMessage live gepatchte, aber ungespeicherte Änderung (Schriftgröße, Farbe, Position) im
  // alten iframe hängen, weil React den unveränderten srcDoc-Wert nicht neu lädt.
  const [snapshotVersion, setSnapshotVersion] = useState(0)
  const [showFinalPdf, setShowFinalPdf] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [pageHeight, setPageHeight] = useState(1000)

  useEffect(() => {
    if (!loaded && stored.data) {
      setL(stored.data.layout)
      setLoaded(true)
    }
  }, [stored.data, loaded])

  // Skalierungsfaktor passend zur Breite des umgebenden Kastens berechnen — der Kasten selbst hat
  // eine völlig andere Form als A4 (breiter als hoch), das Dokument darin bleibt aber immer exakt
  // A4-proportioniert (per `zoom`, siehe Render-Teil unten).
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (w) setScale(w / A4_WIDTH_PX)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Das iframe-HTML wird NUR beim ersten Laden und bei Variantenwechsel neu geholt (übernimmt dabei
  // den aktuellen Bearbeitungsstand) — laufende Änderungen (Drag, Schriftgröße, Farbe) patchen das
  // bereits geladene iframe direkt per postMessage, damit nichts flackert/neu lädt.
  useEffect(() => {
    if (loaded) {
      setSnapshot({ variant: which, layout: L })
      setSnapshotVersion((v) => v + 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, which])

  const htmlQuery = trpc.pdfLayout.previewHtml.useQuery(snapshot ?? { variant: 'invoice', layout: DEFAULT_PDF_LAYOUT }, {
    enabled: !!snapshot,
  })

  useEffect(() => {
    function onMsg(ev: MessageEvent) {
      const d = ev.data as { source?: string; type?: string; el?: ElKey; x?: number; y?: number; draggable?: boolean }
      if (!d || d.source !== 'mavatech-pdf-editor') return
      if (d.type === 'select' && d.el) setSelected(d.el)
      else if (d.type === 'drag' && d.el && d.x != null && d.y != null) {
        const el = d.el
        const x = d.x
        const y = d.y
        setL((prev) => {
          if (el === 'logo') return { ...prev, logo: { ...prev.logo, x, y } }
          if (el === 'senderLine') return { ...prev, senderLine: { ...prev.senderLine, x, y } }
          if (el === 'recipient') return { ...prev, recipient: { ...prev.recipient, x, y } }
          if (el === 'metaBox') return { ...prev, metaBox: { ...prev.metaBox, x, y } }
          if (el === 'title') return { ...prev, title: { ...prev.title, x, y } }
          return prev
        })
      }
    }
    window.addEventListener('message', onMsg)
    return () => window.removeEventListener('message', onMsg)
  }, [])

  const contentRoRef = useRef<ResizeObserver | null>(null)
  const onIframeLoad = () => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'enable-editor' }, '*')
    // Höhe des iframes an den tatsächlichen Inhalt anpassen (Dokument ist unpaginiert/durchlaufend
    // in der Editor-Vorschau) — inkl. Neumessen, wenn sich die Höhe durch eine Live-Änderung
    // (z. B. größere Schrift → Zeilenumbruch) verschiebt.
    contentRoRef.current?.disconnect()
    const doc = iframeRef.current?.contentDocument
    if (!doc) return
    const measure = () => setPageHeight(Math.max(400, doc.documentElement.scrollHeight))
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(doc.documentElement)
    contentRoRef.current = ro
  }
  const pushVar = (name: string, value: string) => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'setvar', name, value }, '*')
  }
  const pushDisplay = (el: string, visible: boolean) => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'update', el, style: { display: visible ? 'block' : 'none' } }, '*')
  }
  const pushTop = (el: string, top: number) => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'update', el, style: { top } }, '*')
  }

  const preview = trpc.pdfLayout.preview.useQuery(L as unknown as Record<string, unknown>, { enabled: showFinalPdf })
  const save = trpc.pdfLayout.save.useMutation({
    onSuccess: (l) => {
      setL(l)
      utils.pdfLayout.get.invalidate()
      addToast({ type: 'success', title: 'PDF-Layout gespeichert' })
    },
    onError: (e) => addToast({ type: 'error', title: 'Fehler', description: e.message }),
  })

  const reset = () => {
    const d = stored.data?.default ?? DEFAULT_PDF_LAYOUT
    setL(d)
    setSnapshot({ variant: which, layout: d })
    setSnapshotVersion((v) => v + 1)
  }

  const finalSrc = (() => {
    const b64 = which === 'invoice' ? preview.data?.invoice : preview.data?.quote
    return b64 ? `data:application/pdf;base64,${b64}#toolbar=0&navpanes=0` : ''
  })()

  return (
    <div className="p-6 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
      {/* ── Live-Vorschau ── */}
      <div className="space-y-2 min-w-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Segmented
            value={which}
            onChange={(v) => {
              setWhich(v)
              setSelected(null)
            }}
            options={[
              { value: 'invoice', label: 'Rechnung' },
              { value: 'quote', label: 'Angebot' },
            ]}
            aria-label="Vorschau-Dokument"
          />
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setShowFinalPdf((v) => !v)
                if (!showFinalPdf) preview.refetch()
              }}
            >
              <FileText className="w-4 h-4" /> {showFinalPdf ? 'Editor anzeigen' : 'Als PDF prüfen'}
            </Button>
            <Button variant="primary" onClick={() => save.mutate(L as unknown as Record<string, unknown>)} disabled={save.isPending}>
              <Save className="w-4 h-4" /> Speichern
            </Button>
            <Button variant="ghost" onClick={reset}>
              <RotateCcw className="w-4 h-4" /> Zurücksetzen
            </Button>
          </div>
        </div>
        <p className="text-xs text-text-tertiary">
          {showFinalPdf
            ? 'So sieht das fertige PDF aus (echtes Rendering).'
            : 'Kopfbereich-Elemente lassen sich anklicken und verschieben. Rechts erscheinen die Eigenschaften des ausgewählten Elements.'}
        </p>
        <div ref={wrapRef} className="border border-border-subtle rounded-lg overflow-auto bg-bg-tertiary" style={{ height: '76vh' }}>
          {showFinalPdf ? (
            finalSrc ? (
              <iframe title="PDF-Vorschau" src={finalSrc} className="w-full h-full" />
            ) : (
              <div className="w-full h-full grid place-items-center text-sm text-text-tertiary">PDF wird gerendert …</div>
            )
          ) : htmlQuery.data ? (
            // Das Dokument ist immer exakt A4-breit (siehe body-Regel in html-template.ts); der
            // umgebende Kasten hat aber eine andere Form — `zoom` skaliert das gesamte A4-Blatt
            // passend herunter/hoch, statt es zu strecken (das ergab vorher eine verzerrte
            // Querformat-Ansicht).
            <div style={{ width: A4_WIDTH_PX, zoom: scale || 1, margin: '0 auto' }}>
              <iframe
                key={snapshotVersion}
                ref={iframeRef}
                title="Editor-Vorschau"
                srcDoc={htmlQuery.data.html}
                onLoad={onIframeLoad}
                className="bg-white"
                style={{ border: 'none', display: 'block', width: '100%', height: pageHeight }}
              />
            </div>
          ) : (
            <div className="w-full h-full grid place-items-center text-sm text-text-tertiary">Vorschau wird geladen …</div>
          )}
        </div>
      </div>

      {/* ── Eigenschaften-Panel ── */}
      <div className="space-y-5">
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-text-primary">Allgemein</h3>
          <label className="flex flex-col gap-1 text-xs text-text-secondary">
            Akzentfarbe (Hex, leer = Branding)
            <input
              type="text"
              placeholder="#2B87C7"
              value={L.accentColor ?? ''}
              onChange={(e) => {
                const v = e.target.value.trim() || null
                setL((p) => ({ ...p, accentColor: v }))
                const resolved = v ?? '#2B87C7'
                pushVar('--accent', resolved)
                pushVar('--footer-bg', darkenHex(resolved))
              }}
              className="bg-bg-input border border-border rounded px-2 py-1.5 text-sm text-text-primary"
            />
          </label>
        </section>

        <section className="space-y-2 border-t border-border-subtle pt-4">
          <h3 className="text-sm font-semibold text-text-primary">
            {selected ? PDF_ELEMENT_LABELS[selected] : 'Kein Element ausgewählt'}
          </h3>
          {!selected && (
            <p className="text-xs text-text-tertiary">
              Klicke links im Dokument auf ein Element (z. B. Logo, Info-Kasten, Positionstabelle), um es hier zu
              bearbeiten.
            </p>
          )}

          {selected === 'logo' && (
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <CheckField
                  label="Logo + Balken auf jeder Folgeseite wiederholen"
                  checked={L.repeatHeader}
                  onChange={(b) => setL((p) => ({ ...p, repeatHeader: b }))}
                />
              </div>
              <NumField
                label="max. Breite"
                value={L.logo.maxW}
                onChange={(n) => {
                  setL((p) => ({ ...p, logo: { ...p.logo, maxW: n } }))
                  pushVar('--logo-maxw', `${n}pt`)
                }}
              />
              <NumField
                label="max. Höhe"
                value={L.logo.maxH}
                onChange={(n) => {
                  setL((p) => ({ ...p, logo: { ...p.logo, maxH: n } }))
                  pushVar('--logo-maxh', `${n}pt`)
                }}
              />
            </div>
          )}

          {selected === 'bar' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <NumField label="Stärke" value={L.bar.thickness} onChange={(n) => { setL((p) => ({ ...p, bar: { ...p.bar, thickness: n } })); pushVar('--bar-thickness', `${n}pt`) }} />
                <NumField label="Y-Position" value={L.bar.y} onChange={(n) => { setL((p) => ({ ...p, bar: { ...p.bar, y: n } })); pushTop('bar', n) }} />
              </div>
              <CheckField label="sichtbar" checked={L.bar.visible} onChange={(b) => { setL((p) => ({ ...p, bar: { ...p.bar, visible: b } })); pushDisplay('bar', b) }} />
              <p className="text-xs text-text-tertiary">Nicht frei verschiebbar (volle Seitenbreite) — Y-Position hier eingeben.</p>
            </>
          )}

          {selected === 'senderLine' && (
            <>
              <NumField label="Schriftgröße" value={L.senderLine.fontSize} step={0.5} onChange={(n) => { setL((p) => ({ ...p, senderLine: { ...p.senderLine, fontSize: n } })); pushVar('--sender-fs', `${n}pt`) }} />
              <CheckField label="sichtbar" checked={L.senderLine.visible} onChange={(b) => { setL((p) => ({ ...p, senderLine: { ...p.senderLine, visible: b } })); pushDisplay('senderLine', b) }} />
            </>
          )}

          {selected === 'recipient' && (
            <NumField label="Schriftgröße" value={L.recipient.fontSize} step={0.5} onChange={(n) => { setL((p) => ({ ...p, recipient: { ...p.recipient, fontSize: n } })); pushVar('--recipient-fs', `${n}pt`) }} />
          )}

          {selected === 'metaBox' && (
            <div className="grid grid-cols-2 gap-2">
              <NumField label="Breite" value={L.metaBox.width} onChange={(n) => { setL((p) => ({ ...p, metaBox: { ...p.metaBox, width: n } })); pushVar('--metabox-w', `${n}pt`) }} />
              <NumField label="Schriftgröße" value={L.metaBox.fontSize} step={0.5} onChange={(n) => { setL((p) => ({ ...p, metaBox: { ...p.metaBox, fontSize: n } })); pushVar('--meta-fs', `${n}pt`) }} />
            </div>
          )}

          {selected === 'title' && (
            <div className="grid grid-cols-2 gap-2">
              <NumField label="Überschrift (RECHNUNG)" value={L.title.fontSize} step={0.5} onChange={(n) => { setL((p) => ({ ...p, title: { ...p.title, fontSize: n } })); pushVar('--title-fs', `${n}pt`) }} />
              <NumField label="Zeile darunter (Betreff)" value={L.title.subtitleFontSize} step={0.5} onChange={(n) => { setL((p) => ({ ...p, title: { ...p.title, subtitleFontSize: n } })); pushVar('--subtitle-fs', `${n}pt`) }} />
            </div>
          )}

          {selected === 'intro' && (
            <NumField label="Schriftgröße" value={L.intro.fontSize} step={0.5} onChange={(n) => { setL((p) => ({ ...p, intro: { ...p.intro, fontSize: n } })); pushVar('--intro-fs', `${n}pt`) }} />
          )}

          {selected === 'table' && (
            <div className="grid grid-cols-2 gap-2">
              <NumField label="Spaltenkopf-Größe" value={L.table.headerFontSize} step={0.5} onChange={(n) => { setL((p) => ({ ...p, table: { ...p.table, headerFontSize: n } })); pushVar('--table-header-fs', `${n}pt`) }} />
              <NumField label="Zeilen-Größe" value={L.table.fontSize} step={0.5} onChange={(n) => { setL((p) => ({ ...p, table: { ...p.table, fontSize: n } })); pushVar('--table-fs', `${n}pt`) }} />
            </div>
          )}

          {selected === 'summary' && (
            <NumField label="Schriftgröße" value={L.summary.fontSize} step={0.5} onChange={(n) => { setL((p) => ({ ...p, summary: { ...p.summary, fontSize: n } })); pushVar('--summary-fs', `${n}pt`) }} />
          )}

          {selected === 'footer' && (
            <>
              <NumField label="Schriftgröße" value={L.footer.fontSize} step={0.5} onChange={(n) => { setL((p) => ({ ...p, footer: { ...p.footer, fontSize: n } })); pushVar('--footer-fs', `${n}pt`) }} />
              <NumField label="Balkenhöhe in pt (0 = automatisch)" value={L.footer.height} step={2} onChange={(n) => { const v = Math.min(200, Math.max(0, n)); setL((p) => ({ ...p, footer: { ...p.footer, height: v } })); pushVar('--footer-h', `${v}pt`) }} />
              <CheckField label="sichtbar" checked={L.footer.visible} onChange={(b) => { setL((p) => ({ ...p, footer: { ...p.footer, visible: b } })); pushDisplay('footer', b) }} />
            </>
          )}

          {selected === 'qrCode' && (
            <>
              <NumField label="Größe (pt)" value={L.qrCode.size} step={5} onChange={(n) => { const v = Math.min(150, Math.max(50, n)); setL((p) => ({ ...p, qrCode: { ...p.qrCode, size: v } })); pushVar('--qr-size', `${v}pt`) }} />
              <CheckField label="sichtbar" checked={L.qrCode.visible} onChange={(b) => { setL((p) => ({ ...p, qrCode: { ...p.qrCode, visible: b } })); pushDisplay('qrCode', b) }} />
              <p className="text-xs text-text-tertiary">Erscheint nur auf Rechnungen mit offenem Betrag (nicht bei Entwurf, Gutschrift oder bezahlt).</p>
            </>
          )}

          {selected && DRAGGABLE.has(selected) && (
            <p className="text-xs text-text-tertiary pt-1">Direkt im Dokument links per Ziehen verschiebbar.</p>
          )}
        </section>
      </div>
    </div>
  )
}
